import { useState } from 'react'
import { portalAPI } from '../lib/api'
import { Input } from '../components/ui/Input'
import { Button } from '../components/ui/Button'
import { Mail, KeyRound, ArrowLeft, CheckCircle, AlertCircle, Sparkles } from 'lucide-react'

export default function PortalLoginPage() {
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [step, setStep] = useState<'request'|'verify'>('request')
  const [message, setMessage] = useState<string>('')
  const [error, setError] = useState<string>('')
  const [loading, setLoading] = useState(false)

  async function requestCode(e: React.FormEvent) {
    e.preventDefault()
    setError(''); setMessage(''); setLoading(true)
    const res = await portalAPI.requestOtp(email)
    setLoading(false)
    if (res.success) {
      setMessage('Check your email for a 6-digit code.');
      setStep('verify')
    }
    else setError(res.message || 'Failed to request code')
  }

  async function verify(e: React.FormEvent) {
    e.preventDefault()
    setError(''); setMessage(''); setLoading(true)
    const res = await portalAPI.verifyOtp(email, code)
    setLoading(false)
    if (res.success) {
      setMessage('Successfully logged in! Redirecting...');
      setTimeout(() => {
        window.location.href = '/portal/bookings'
      }, 1500)
    }
    else setError(res.message || 'Invalid code')
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-pink-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-red-500 mb-4">
            <Sparkles className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent mb-2">
            Customer Portal
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Access your bookings and loyalty rewards
          </p>
        </div>

        {/* Login Card */}
        <div className="card border-2 border-orange-200 dark:border-orange-800 shadow-xl">
          <div className="card-body">
            {step === 'request' && (
              <form onSubmit={requestCode} className="space-y-4">
                <div>
                  <label className="form-label flex items-center gap-2">
                    <Mail className="h-4 w-4 text-gray-500" />
                    Email Address
                  </label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your.email@example.com"
                    required
                    disabled={loading}
                    className="text-lg"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    We'll send a verification code to your email
                  </p>
                </div>
                <Button
                  type="submit"
                  disabled={loading || !email.trim()}
                  className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white flex items-center justify-center gap-2"
                >
                  <Mail className="h-4 w-4" />
                  {loading ? 'Sending...' : 'Send Verification Code'}
                </Button>
              </form>
            )}

            {step === 'verify' && (
              <form onSubmit={verify} className="space-y-4">
                <div className="p-3 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-sm text-blue-800 dark:text-blue-300 flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Code sent to <span className="font-semibold">{email}</span>
                  </p>
                </div>

                <div>
                  <label className="form-label flex items-center gap-2">
                    <KeyRound className="h-4 w-4 text-gray-500" />
                    6-Digit Verification Code
                  </label>
                  <Input
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="000000"
                    required
                    disabled={loading}
                    className="text-2xl tracking-widest text-center font-mono"
                    maxLength={6}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter the code from your email
                  </p>
                </div>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {setStep('request'); setCode(''); setError(''); setMessage('')}}
                    disabled={loading}
                    className="flex items-center gap-2"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading || code.length !== 6}
                    className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white flex items-center justify-center gap-2"
                  >
                    <KeyRound className="h-4 w-4" />
                    {loading ? 'Verifying...' : 'Verify & Sign In'}
                  </Button>
                </div>
              </form>
            )}

            {/* Success Message */}
            {message && (
              <div className="mt-4 p-3 bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-800 rounded-lg flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-800 dark:text-green-200">{message}</p>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mt-4 p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-800 dark:text-red-200">{error}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-gray-600 dark:text-gray-400">
          <p>Powered by <span className="font-semibold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">StudioManager 360</span></p>
        </div>
      </div>
    </div>
  )
}
